import {
  IUserSettingUpdateMutation,
  IUserSettingUpdateMutationVariables,
  LlmSubjectKind,
  UserSettingKind,
} from '../../utils/apollo/graphqlSchema';
import Scratchpad from '../Scratchpad';
import { useAppDispatch } from '../../redux/hooks';
import { setSidebarState, SidebarState } from '../../redux/slices/userSettings';
import { USER_SETTING_UPDATE } from '../../utils/apollo/userSettings';
import { CONTAINER_WIDTH, FixedContainer } from './RightSideBarUI';
import LumosIcon from './LumosIcon';
import ExplainEntityWithAiPanel from '../ExplainWithAi/ExplainEntityWithAiPanel';
import { useUserSettingsSelector } from '../../redux/selectorHooks';
import { explainWithAiRightSideBarTooltips } from '../KubitTooltip/globalTooltips';
import { useCallback } from 'react';
import QuickAccessDictionary from '../QuickAccessDictionary';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import CollapsableSideBar from '../CollapsableSideBar';
import RightSideBarIcon from './RightSideBarIcon';
import { useShowComments, useShowLumos } from './utils';
import DashboardComments from './DashboardComments';
import CommentsToggle from './CommentsToggle';

const Container = styled.div`
  flex: 0 0 ${CONTAINER_WIDTH}px;
  width: ${CONTAINER_WIDTH}px;
`;

const RightSideBar = () => {
  const dispatch = useAppDispatch();

  const { sidebarState } = useUserSettingsSelector('sidebarState');

  const showLumos = useShowLumos();
  const showComments = useShowComments();

  const [saveSettings] = useMutation<
    IUserSettingUpdateMutation,
    IUserSettingUpdateMutationVariables
  >(USER_SETTING_UPDATE);

  const changeSidebarState = useCallback(
    (newState: SidebarState) => {
      const value =
        sidebarState === newState ? SidebarState.Collapsed : newState;

      dispatch(setSidebarState(value));

      saveSettings({
        variables: {
          input: {
            userSettings: [
              {
                kind: UserSettingKind.SidebarPinned,
                value,
              },
            ],
          },
        },
      });
    },
    [dispatch, saveSettings, sidebarState],
  );

  const toggleScratchpad = useCallback(() => {
    changeSidebarState(SidebarState.Scratchpad);
  }, [changeSidebarState]);

  const toggleDictionary = useCallback(() => {
    changeSidebarState(SidebarState.Dictionary);
  }, [changeSidebarState]);

  const toggleLumos = useCallback(() => {
    changeSidebarState(SidebarState.ExplainWithAi);
  }, [changeSidebarState]);

  const toggleComments = useCallback(() => {
    changeSidebarState(SidebarState.Comments);
  }, [changeSidebarState]);

  return (
    <>
      <CollapsableSideBar isExpanded={sidebarState === SidebarState.Dictionary}>
        <QuickAccessDictionary onClose={toggleDictionary} />
      </CollapsableSideBar>

      {showLumos && (
        <CollapsableSideBar
          isExpanded={sidebarState === SidebarState.ExplainWithAi}
        >
          <ExplainEntityWithAiPanel subjectKind={LlmSubjectKind.Analysis} />
        </CollapsableSideBar>
      )}

      <Scratchpad isExpanded={sidebarState === SidebarState.Scratchpad} />

      {showComments && (
        <CollapsableSideBar isExpanded={sidebarState === SidebarState.Comments}>
          <DashboardComments />
        </CollapsableSideBar>
      )}

      <Container>
        <FixedContainer>
          <RightSideBarIcon
            active={sidebarState === SidebarState.Dictionary}
            onClick={toggleDictionary}
            icon="book-open-text"
            content="Quick Access Dictionary"
          />
          <RightSideBarIcon
            active={sidebarState === SidebarState.Scratchpad}
            onClick={toggleScratchpad}
            icon="note"
            content="Scratchpad"
            trackContext="scratchpad"
            trackAction={
              sidebarState !== SidebarState.Scratchpad ? 'open' : 'close'
            }
          />
          {showLumos && (
            <LumosIcon
              tooltipContent={explainWithAiRightSideBarTooltips.enabled}
              onClick={toggleLumos}
              isExpanded={sidebarState === SidebarState.ExplainWithAi}
            />
          )}
          {showComments && (
            <CommentsToggle
              isActive={sidebarState === SidebarState.Comments}
              onClick={toggleComments}
            />
          )}
        </FixedContainer>
      </Container>
    </>
  );
};

export default RightSideBar;
